package org.catharsis.spendtel.core.platform

import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException
import kotlin.coroutines.suspendCoroutine
import kotlinx.browser.document
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext
import org.catharsis.spendtel.core.utils.MAX_STATEMENT_FILE_SIZE_BYTES
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.w3c.dom.HTMLInputElement
import org.w3c.files.FileReader

actual suspend fun pickStatementDocuments(): Result<List<PickedStatementFile>> {
    return try {
        Result.success(openFilePicker())
    } catch (error: Exception) {
        Result.failure(error)
    }
}

private suspend fun openFilePicker(): List<PickedStatementFile> = suspendCoroutine { continuation ->
    val input = document.createElement("input") as HTMLInputElement
    input.type = "file"
    input.multiple = true
    input.accept = ".pdf,.csv,application/pdf,text/csv"
    input.style.display = "none"
    document.body?.appendChild(input)

    input.onchange = {
        document.body?.removeChild(input)
        val fileList = input.files
        if (fileList == null || fileList.length == 0) {
            continuation.resume(emptyList())
        } else {
            GlobalScope.launch(Dispatchers.Default) {
                try {
                    val results = readPickedFiles(fileList)
                    continuation.resume(results)
                } catch (error: Exception) {
                    continuation.resumeWithException(error)
                }
            }
        }
    }

    input.oncancel = {
        document.body?.removeChild(input)
        continuation.resume(emptyList())
    }

    input.click()
}

private suspend fun readPickedFiles(
    fileList: org.w3c.files.FileList,
): List<PickedStatementFile> = withContext(Dispatchers.Default) {
    val results = mutableListOf<PickedStatementFile>()
    for (index in 0 until fileList.length) {
        val file = fileList.item(index) ?: continue
        if (file.size.toLong() > MAX_STATEMENT_FILE_SIZE_BYTES) {
            throw Exception("${file.name} exceeds the 20 MB limit.")
        }

        val bytes = file.readBytes()
        if (bytes.isEmpty()) {
            throw Exception("${file.name} is empty.")
        }

        results.add(
            PickedStatementFile(
                fileName = file.name,
                mimeType = file.type.ifBlank {
                    when {
                        file.name.lowercase().endsWith(".pdf") -> "application/pdf"
                        file.name.lowercase().endsWith(".csv") -> "text/csv"
                        else -> "application/octet-stream"
                    }
                },
                bytes = bytes,
                sizeBytes = file.size.toLong(),
            ),
        )
    }
    results
}

private suspend fun org.w3c.files.Blob.readBytes(): ByteArray = suspendCoroutine { continuation ->
    val reader = FileReader()
    reader.onload = {
        val buffer = reader.result as ArrayBuffer
        val int8Array = Int8Array(buffer)
        val bytes = ByteArray(int8Array.length)
        for (index in 0 until int8Array.length) {
            bytes[index] = int8Array.asDynamic()[index] as Byte
        }
        continuation.resume(bytes)
    }
    reader.onerror = {
        continuation.resumeWithException(Exception("Failed to read the selected file."))
    }
    reader.readAsArrayBuffer(this)
}
